import type { ChainData, CosmosChain, Token } from "@0xsquid/squid-types";
import { ChainType } from "@0xsquid/squid-types";
import { ChainNotConfiguredError, Connector } from "@wagmi/core";
import { isAddress, SwitchChainError } from "viem";
import { chainTypeToDefaultChainIdMap } from "../../core/constants";
import { IBitcoinContextData } from "../../core/providers/BitcoinProvider";
import { ICantonContextData } from "../../core/providers/CantonProvider";
import { ICosmosContextData } from "../../core/providers/CosmosProvider";
import { IEvmContextData } from "../../core/providers/EvmProvider";
import { ISolanaContextData } from "../../core/providers/SolanaProvider";
import { IStellarContextData } from "../../core/providers/StellarProvider";
import { ISuiContextData } from "../../core/providers/SuiProvider";
import { IXrplContextData } from "../../core/providers/XrplProvider";
import { TokenWithBalance } from "../../core/types/tokens";
import type {
  BitcoinWallet,
  CantonWallet,
  ChainTypeToWalletState,
  ConnectorID,
  ConnectWalletResult,
  CosmosWallet,
  EnsSearchResult,
  EvmWallet,
  SolanaWallet,
  StellarWallet,
  SuiWallet,
  Wallet,
  WindowWalletFlag,
  XrplWallet,
} from "../../core/types/wallet";
import { fallbackWallets } from "../../core/fallbackWallets";
import { walletStoreLinks } from "../../core/wallets";
import { WidgetEvents } from "../../services/internal/eventService";
import { isBitcoinAddressValid } from "./bitcoinService";
import {
  CANTON_PARTY_ID_SEPARATOR,
  isCantonAddressValid,
} from "./cantonService";
import { connectCosmosWallet, isCosmosAddressValid } from "./cosmosService";
import { isSolanaAddressValid } from "./solanaService";
import { isStellarAddressValid } from "./stellarService";
import { isSuiAddressValid } from "./suiService";
import { isXrplAddressValid } from "./xrplService";

type FormatHashParams = {
  hash?: string;
  chainType?: ChainType;
};

const chainTypeToTrimLength: Record<
  ChainType,
  {
    start: number;
    end: number;
  }
> = {
  // 0x1234...5678
  [ChainType.EVM]: {
    start: 6,
    end: 4,
  },
  // osmo1234...5678
  [ChainType.COSMOS]: {
    start: 8,
    end: 4,
  },
  // 1A1zP1eP...vfNa
  [ChainType.BTC]: {
    start: 8,
    end: 4,
  },
  // 6fq5...AmcC
  [ChainType.SOLANA]: {
    start: 4,
    end: 4,
  },
  // 0x1234...5678
  [ChainType.SUI]: {
    start: 6,
    end: 4,
  },
  // r9uP...s6mN
  [ChainType.XRPL]: {
    start: 4,
    end: 4,
  },
  // GABC...N7F5
  [ChainType.STELLAR]: {
    start: 4,
    end: 4,
  },
  // abc...123::abc...123
  [ChainType.CANTON]: {
    start: 3,
    end: 3,
  },
};

const truncateWithEllipsis = (
  value: string,
  startLength: number,
  endLength: number
): string =>
  value.length <= startLength + endLength
    ? value
    : value.slice(0, startLength) +
      "..." +
      value.slice(value.length - endLength);

export const formatHash = ({ chainType, hash }: FormatHashParams): string => {
  if (!hash) return "";

  let chainTypeFormat = chainType as ChainType | undefined;

  if (!chainType) {
    if (hash?.startsWith("0x")) {
      chainTypeFormat = ChainType.EVM;
    } else {
      chainTypeFormat = ChainType.COSMOS;
    }
  }

  const { start, end } = chainTypeToTrimLength[chainTypeFormat!];

  if (
    chainTypeFormat === ChainType.CANTON &&
    hash.includes(CANTON_PARTY_ID_SEPARATOR)
  ) {
    const [namespace, identifier] = hash.split(CANTON_PARTY_ID_SEPARATOR);
    return (
      truncateWithEllipsis(namespace, start, end) +
      CANTON_PARTY_ID_SEPARATOR +
      truncateWithEllipsis(identifier, start, end)
    );
  }

  return truncateWithEllipsis(hash, start, end);
};

export const isWalletAddressValid = (
  chainData?: ChainData,
  address?: string
): boolean => {
  if (!address || !chainData?.chainType) return false;

  switch (chainData.chainType) {
    case ChainType.EVM:
      return isAddress(address);
    case ChainType.COSMOS:
      return isCosmosAddressValid(
        (chainData as CosmosChain).bech32Config.bech32PrefixAccAddr,
        address
      );
    case ChainType.BTC:
      return isBitcoinAddressValid(address);
    case ChainType.SOLANA:
      return isSolanaAddressValid(address);
    case ChainType.SUI:
      return isSuiAddressValid(address);
    case ChainType.XRPL:
      return isXrplAddressValid(address);
    case ChainType.STELLAR:
      return isStellarAddressValid(address);
    case ChainType.CANTON:
      return isCantonAddressValid(address);
  }
};

export const redirectToExtensionsStore = (wallet: Wallet) => {
  const { userAgent } = navigator;
  let link: string | undefined;

  if (userAgent.indexOf("Firefox") > -1) {
    link =
      wallet.links?.firefox || walletStoreLinks[wallet.connectorId]?.firefox;
  } else if (userAgent.indexOf("Chrome") > -1) {
    link = wallet.links?.chrome || walletStoreLinks[wallet.connectorId]?.chrome;
  }

  link = link || wallet.links?.website;

  if (link && link !== "") {
    (window as any)?.open(link, "_blank")?.focus();
  }
};

export const definedInWindow = (
  path: string | undefined,
  clientWindow: Window | undefined
): boolean => {
  if (!path || !clientWindow) return false;

  const parts = path.split(".");

  let obj: any = clientWindow;

  for (const part of parts) {
    if (obj && typeof obj === "object" && part in obj) {
      obj = obj[part as keyof typeof obj];
    } else {
      return false;
    }
  }

  return Boolean(obj);
};

export const populateWallets = <T extends Wallet>(
  window: Window | undefined,
  wallets: T[]
): T[] => {
  return wallets.map((wallet) => {
    if (!wallet.isInstalled) {
      return {
        ...wallet,
        isInstalled() {
          return definedInWindow(wallet.windowFlag, window);
        },
      };
    }

    return wallet;
  });
};

const createUninstalledConnector = () =>
  new Proxy(
    {},
    {
      get(_target, prop) {
        throw new Error(
          `Cannot use connector of an uninstalled wallet (accessed "${String(
            prop
          )}")`
        );
      },
    }
  );

export const buildFallbackWallets = (discovered: Wallet[]): Wallet[] => {
  const fallbackKey = (chainType: ChainType, connectorId: string) =>
    `${chainType}:${connectorId}`;

  const discoveredKeys = new Set(
    discovered.flatMap((wallet) =>
      wallet.isMultiChain
        ? wallet.supportedNetworks.map((network) =>
            fallbackKey(network.chainType, wallet.connectorId)
          )
        : [fallbackKey(wallet.type, wallet.connectorId)]
    )
  );

  return fallbackWallets
    .filter(
      (config) => !discoveredKeys.has(fallbackKey(config.chainType, config.id))
    )
    .map(
      (config): Wallet => ({
        name: config.name,
        connectorId: config.id as ConnectorID,
        connectorName: config.connectorName,
        icon: config.icon,
        windowFlag: config.id as unknown as WindowWalletFlag,
        links: config.links,
        isInstalled: () => false,
        skipInstallCheck: false,
        isMultiChain: false,
        type: config.chainType,
        connector: createUninstalledConnector() as never,
      })
    );
};

/**
 * Get the base chain to connect to
 * If only one chain type is selected, use that chain type
 * Otherwise, use the wallet's type
 * This is mainly used for Cosmos wallets, they need a reference chain to connect to (because addresses are different on each chain)
 * @param wallet
 * @param chain
 * @param selectedChainTypes
 * @param findChain
 * @returns
 */
export const getDefaultChain = (
  wallet: Wallet,
  chain: ChainData | undefined,
  selectedChainTypes: ChainType[] | undefined,
  findChain: (chainId: string) => ChainData | undefined
): ChainData | undefined => {
  // If a chain is provided, use that chain
  if (chain) return chain;

  let walletType = wallet.isMultiChain ? undefined : wallet.type;

  // If only one chain type is selected, use that chain type
  if (selectedChainTypes?.length === 1) {
    const chainType = selectedChainTypes[0];
    walletType = chainType;
  }

  if (!walletType) return undefined;

  return findChain(chainTypeToDefaultChainIdMap[walletType]);
};

/**
 * Sort order:
 * 1. Connected wallets
 * 2. Installed wallets
 * 3. Priority wallets
 * 4. The rest
 *
 * On mobile, Injected and WalletConnect are always on top,
 * after installed wallets.
 *
 * @returns The sorted wallets
 */
export function sortWallets(
  a: Pick<Wallet, "connectorId" | "isInstalled">,
  b: Pick<Wallet, "connectorId" | "isInstalled">,
  {
    isMobile,
    connectedWallets,
    priorityList = [],
  }: {
    isMobile: boolean;
    connectedWallets?: Pick<Wallet, "connectorId">[];
    priorityList?: Wallet["connectorId"][];
  }
) {
  // Sort connected wallets first
  const aIsConnected = connectedWallets?.some(
    (w) => w.connectorId === a.connectorId
  );
  const bIsConnected = connectedWallets?.some(
    (w) => w.connectorId === b.connectorId
  );

  if (aIsConnected && !bIsConnected) return -1;
  if (!aIsConnected && bIsConnected) return 1;

  // Then sort installed wallets
  const aIsInstalled = a.isInstalled?.();
  const bIsInstalled = b.isInstalled?.();

  if (aIsInstalled && !bIsInstalled) return -1;
  if (!aIsInstalled && bIsInstalled) return 1;

  // Sort by priority list
  const aPriorityIndex = priorityList.indexOf(a.connectorId);
  const bPriorityIndex = priorityList.indexOf(b.connectorId);

  if (aPriorityIndex !== -1 || bPriorityIndex !== -1) {
    if (aPriorityIndex === -1) return 1;
    if (bPriorityIndex === -1) return -1;
    return aPriorityIndex - bPriorityIndex;
  }

  // Mobile-specific sorting
  if (isMobile) {
    if (a.connectorId === "injected" && b.connectorId !== "injected") return -1;
    if (a.connectorId !== "injected" && b.connectorId === "injected") return 1;
    if (a.connectorId === "walletConnect" && b.connectorId !== "walletConnect")
      return -1;
    if (a.connectorId !== "walletConnect" && b.connectorId === "walletConnect")
      return 1;
  }

  return 0;
}

export function areSameAddress(address1?: string, address2?: string) {
  if (!address1 || !address2) return false;

  return address1.trim().toLowerCase() === address2.trim().toLowerCase();
}

export function sortAddressBook(
  a: EnsSearchResult,
  b: EnsSearchResult,
  selectedAddress?: string
): 1 | 0 | -1 {
  if (!selectedAddress) return 0;

  if (areSameAddress(a.address, selectedAddress)) {
    return -1;
  }

  if (areSameAddress(b.address, selectedAddress)) {
    return 1;
  }

  return 0;
}

export const calculateTotalUsdBalanceUSD = (
  tokens: Pick<TokenWithBalance, "balance" | "usdPrice">[]
): string => {
  const totalUsd = tokens.reduce((total, token) => {
    const balance = parseFloat(token.balance);
    const usdPrice = token.usdPrice ?? 0;
    const balanceUsd = balance * usdPrice;
    return total + balanceUsd;
  }, 0);

  // Convert to string with high precision
  const preciseTotal = totalUsd.toFixed(18);

  // Remove trailing zeros and decimal point if necessary
  const trimmedTotal = preciseTotal.replace(/\.?0+$/, "");

  return trimmedTotal;
};

export function addTokenToWallet({
  token: { address, decimals, symbol, logoURI },
  provider,
}: {
  token: Pick<Token, "address" | "symbol" | "decimals" | "logoURI">;
  provider: any;
}) {
  if (!address || !symbol || !decimals || !logoURI) return;

  try {
    return provider.request({
      method: "wallet_watchAsset",
      params: {
        type: "ERC20",
        options: {
          address,
          symbol,
          decimals,
          image: logoURI,
        },
      },
    });
  } catch (error) {
    console.debug("Error adding token to wallet:", error);
  }
}

export function isEvmChainNotSupportedError(error: any) {
  return (
    error instanceof ChainNotConfiguredError ||
    error instanceof SwitchChainError
  );
}

export const getWalletSupportedChainTypes = (wallet: Wallet): ChainType[] => {
  if (!wallet.isMultiChain) return [];

  return wallet.supportedNetworks.map((config) => config.chainType);
};

export const getConnectorForChainType = <T extends ChainType>(
  wallet: Wallet,
  chainType: T
): ChainTypeToWalletState[T]["wallet"]["connector"] | undefined => {
  // For multi-chain wallets
  if (wallet.isMultiChain) {
    const networkConfig = wallet.supportedNetworks.find(
      (config) => config.chainType === chainType
    );
    return networkConfig?.connector;
  }

  // For single-chain wallets, return the wallet's connector if chain type matches
  if (wallet.type === chainType) {
    return (wallet as ChainTypeToWalletState[T]["wallet"]).connector;
  }

  return undefined;
};

export function walletSupportsChainType(wallet: Wallet, chainType: ChainType) {
  if (wallet.isMultiChain) {
    return wallet.supportedNetworks.some(
      (network) => network.chainType === chainType
    );
  }

  return wallet.type === chainType;
}

const connectByChainType = async (
  chainType: ChainType,
  wallet: Wallet,
  defaultChain: ChainData | undefined,
  params: ConnectWalletMutations
): Promise<ConnectWalletResult<ChainType> | null> => {
  if (!chainType) {
    throw new Error(`Unsupported wallet type: ${chainType}`);
  }

  // Get connector for chain type if it's a multi-chain wallet
  const connector = getConnectorForChainType(wallet, chainType);

  switch (chainType) {
    case ChainType.EVM:
      return params.connectEvm.mutateAsync({
        wallet: {
          ...wallet,
          connector,
        } as EvmWallet,
      });

    case ChainType.COSMOS:
      return connectCosmosWallet(
        { ...wallet, connector } as CosmosWallet,
        defaultChain,
        {
          connectCosmos: params.connectCosmos,
          cosmosConnectedWallet: params.cosmosConnectedWallet as CosmosWallet,
        }
      );

    case ChainType.SOLANA:
      return params.connectSolana.mutateAsync({
        wallet: {
          ...wallet,
          connector,
        } as SolanaWallet,
      });

    case ChainType.BTC:
      return params.connectBitcoin.mutateAsync({
        wallet: {
          ...wallet,
          connector,
        } as BitcoinWallet,
      });

    case ChainType.SUI:
      return params.connectSui.mutateAsync({
        wallet: {
          ...wallet,
          connector,
        } as SuiWallet,
      });

    case ChainType.XRPL:
      return params.connectXrpl.mutateAsync({
        wallet: {
          ...wallet,
          connector,
        } as XrplWallet,
      });

    case ChainType.STELLAR:
      return params.connectStellar.mutateAsync({
        wallet: {
          ...wallet,
          connector,
        } as StellarWallet,
      });

    case ChainType.CANTON:
      return params.connectCanton.mutateAsync({
        wallet: {
          ...wallet,
          connector,
        } as CantonWallet,
      });
  }
};

interface ConnectWalletMutations {
  connectEvm: IEvmContextData["connectEvm"];
  connectCosmos: ICosmosContextData["connectCosmos"];
  cosmosConnectedWallet: ICosmosContextData["cosmosConnectedWallet"];
  connectSolana: ISolanaContextData["connectSolana"];
  connectBitcoin: IBitcoinContextData["connectBitcoin"];
  connectSui: ISuiContextData["connectSui"];
  connectXrpl: IXrplContextData["connectXrpl"];
  connectStellar: IStellarContextData["connectStellar"];
  connectCanton: ICantonContextData["connectCanton"];
}

type ConnectWalletParams = {
  wallet: Wallet;
  selectedChain?: ChainData;
  selectedChainTypes?: ChainType[];
  findChain: (chainId: string) => ChainData | undefined;
} & ConnectWalletMutations;

interface WalletConnectionResult {
  wallet: Wallet;
  addresses: Array<{
    chainType: ChainType;
    data: ConnectWalletResult<ChainType>;
  }>;
}

const getChainTypesToConnect = (
  selectedChainTypes: ChainType[] | undefined,
  defaultChain: ChainData | undefined,
  supportedChains: { chainType: ChainType }[]
): ChainType[] => {
  if (selectedChainTypes?.length) {
    return selectedChainTypes;
  }

  if (defaultChain) {
    return [defaultChain.chainType];
  }

  return supportedChains.map((chain) => chain.chainType);
};

const connectMultiChainWallet = async (
  params: ConnectWalletParams
): Promise<WalletConnectionResult | null> => {
  if (!params.wallet.isMultiChain) {
    return null;
  }

  const results = [];

  // This should be defined, but in some cases (like widget connection) it's not
  const supportedChains = params.wallet.supportedNetworks;

  // Depending on params, we need to connect to specific chains or just one
  const chainTypesToConnect = getChainTypesToConnect(
    params.selectedChainTypes,
    params.selectedChain,
    supportedChains
  );

  // Connect to each chain type
  for (const chainConfig of supportedChains) {
    if (!chainTypesToConnect.includes(chainConfig.chainType)) {
      continue;
    }

    const defaultChain = getDefaultChain(
      params.wallet,
      params.selectedChain,
      [chainConfig.chainType],
      params.findChain
    );

    try {
      const result = await connectByChainType(
        chainConfig.chainType,
        params.wallet,
        defaultChain,
        {
          connectEvm: params.connectEvm,
          connectCosmos: params.connectCosmos,
          cosmosConnectedWallet: params.cosmosConnectedWallet,
          connectSolana: params.connectSolana,
          connectBitcoin: params.connectBitcoin,
          connectSui: params.connectSui,
          connectXrpl: params.connectXrpl,
          connectStellar: params.connectStellar,
          connectCanton: params.connectCanton,
        }
      );
      if (result) {
        results.push({
          chainType: chainConfig.chainType,
          data: result,
        });
      }
    } catch (error) {
      console.error(`Failed to connect ${chainConfig.chainType}:`, error);
    }
  }

  return results.length > 0
    ? {
        wallet: params.wallet,
        addresses: results,
      }
    : null;
};

const connectSingleChainWallet = async (
  params: ConnectWalletParams
): Promise<WalletConnectionResult | null> => {
  if (params.wallet.isMultiChain) {
    return null;
  }

  const defaultChain = params.selectedChain;

  const result = await connectByChainType(
    params.wallet.type,
    params.wallet,
    defaultChain,
    {
      connectEvm: params.connectEvm,
      connectCosmos: params.connectCosmos,
      cosmosConnectedWallet: params.cosmosConnectedWallet,
      connectSolana: params.connectSolana,
      connectBitcoin: params.connectBitcoin,
      connectSui: params.connectSui,
      connectXrpl: params.connectXrpl,
      connectStellar: params.connectStellar,
      connectCanton: params.connectCanton,
    }
  );

  return result
    ? {
        wallet: params.wallet,
        addresses: [
          {
            chainType: params.wallet.type,
            data: result,
          },
        ],
      }
    : null;
};

export const connectWallet = async (
  params: ConnectWalletParams
): Promise<WalletConnectionResult | null> => {
  try {
    const result = params.wallet.isMultiChain
      ? await connectMultiChainWallet(params)
      : await connectSingleChainWallet(params);

    if (result) {
      // Dispatch wallet connect event for each connected address
      result.addresses.forEach(({ chainType, data }) => {
        WidgetEvents.getInstance().dispatchWalletConnect({
          walletAddress: data.address,
          chainType: chainType,
          provider: params.wallet.connectorId,
        });
      });
    }

    return result;
  } catch (error) {
    // Dispatch wallet reject event
    if (params.selectedChain) {
      WidgetEvents.getInstance().dispatchWalletReject({
        chainId: params.selectedChain.chainId,
        provider: params.wallet.name,
        reason: error instanceof Error ? error.message : "Unknown error",
      });
    }
    throw error;
  }
};

/**
 * Wallets may have a cancelConnect method
 * This is used to cancel a pending connection and clean up resources
 * (e.g. network requests, event listeners, etc.)
 */
export const cancelConnectWallet = (wallet: Wallet): void => {
  // TODO: implement cancel connect for multi-chain wallets
  if (wallet.isMultiChain) {
    return;
  }

  switch (wallet.type) {
    case ChainType.XRPL:
      return wallet.connector.cancelConnect?.();
    default:
      return;
  }
};

// Issues with Rabby wallet on some chains
// we need to wait some time before getting transaction receipt
// https://github.com/ethers-io/ethers.js/issues/4760
export const isProblematicConnector = (connector: Connector | undefined) => {
  return (
    connector?.id != null &&
    // TODO: inconsistent connector ID behavior, investigate and debug Wagmi
    ["rabby", "io.rabby"].includes(connector.id)
  );
};

interface MergeWalletsParams {
  singleChainWallets: Wallet[];
  multiChainWallets: Wallet[];
}

export function mergeWallets({
  multiChainWallets,
  singleChainWallets,
}: MergeWalletsParams): Wallet[] {
  const mergedWallets = [...multiChainWallets, ...singleChainWallets];

  // if mergedWallets includes single-chain wallets with duplicate connector IDs
  // create a new multi-chain wallet with the same connector ID and add all supported networks
  const unifiedWallets = mergedWallets.reduce((acc, wallet) => {
    const existingWallet = acc.find(
      (w) => w.connectorId === wallet.connectorId
    );

    if (existingWallet && !wallet.isMultiChain) {
      if (existingWallet.isMultiChain) {
        // if existing wallet is already a multi-chain wallet
        // we just need to add the new supported network
        if (
          existingWallet.supportedNetworks.every(
            (network) => network.chainType !== wallet.type
          )
        ) {
          // remove the existing wallet from the array
          acc.splice(acc.indexOf(existingWallet), 1);

          // add the new wallet with all supported networks
          acc.push({
            isMultiChain: true,
            supportedNetworks: [
              ...existingWallet.supportedNetworks,
              {
                chainType: wallet.type,
                connector: wallet.connector as any,
              },
            ],
            // add common properties
            connectorId: existingWallet.connectorId,
            name: existingWallet.name,
            icon: existingWallet.icon,
            windowFlag: existingWallet.windowFlag,
            connectorName: existingWallet.connectorName,
            isInstalled: existingWallet.isInstalled,
            skipInstallCheck: existingWallet.skipInstallCheck,
            isMobile: existingWallet.isMobile,
            rdns: existingWallet.rdns,
          });
        }
      } else {
        // otherwise, if existing wallet is a single-chain wallet
        // we need to create a new multi-chain wallet with the same connector ID

        // remove the existing wallet from the array
        acc.splice(acc.indexOf(existingWallet), 1);

        // add the new wallet with all supported networks
        if (existingWallet.type !== wallet.type) {
          acc.push({
            isMultiChain: true,
            supportedNetworks: [
              {
                chainType: existingWallet.type,
                connector: existingWallet.connector as any,
              },
              {
                chainType: wallet.type,
                connector: wallet.connector as any,
              },
            ],
            // add common properties
            connectorId: existingWallet.connectorId,
            name: existingWallet.name,
            icon: existingWallet.icon,
            windowFlag: existingWallet.windowFlag,
            connectorName: existingWallet.connectorName,
            isInstalled: existingWallet.isInstalled,
            skipInstallCheck: existingWallet.skipInstallCheck,
            isMobile: existingWallet.isMobile,
            rdns: existingWallet.rdns,
          });
        }
      }
    } else {
      acc.push(wallet);
    }

    return acc;
  }, [] as Wallet[]);

  return unifiedWallets;
}
